import { createContext, useContext, useState, useRef, useCallback, useEffect } from 'react'
import { submitPdfJob, pollPdfStatus, downloadPdf } from '../services/pdfExport'

/** Concurrent duplicate submits (double-click, Strict Mode overlap) */
const _pdfIdemInFlight = new Set()
/** Brief cooldown after a successful submit — blunts auto+click/Strict remount without blocking retries after failure */
const _pdfIdemCooldownUntil = new Map()
const PDF_IDEM_COOLDOWN_MS = 900

/** Module-level: survives Strict Mode remounts so the same job never triggers two browser downloads */
const _downloadTriggered = new Set()

/**
 * Context + provider for async PDF export lifecycle.
 * Supports multiple concurrent jobs — each submission is tracked independently.
 *
 * Usage:
 *   1. Wrap the app in <PdfExportProvider>
 *   2. Call usePdfExport() to get { submit, dismiss, jobs }
 *
 * Each job shape: { jobId, status, position?, error?, noCredits?, creditsRemaining? }
 *   status: "submitting" | "queued" | "rendering" | "completed" | "failed"
 */

const PdfExportContext = createContext(null)

export function PdfExportProvider({ children }) {
  useEffect(() => {
    try {
      Object.keys(sessionStorage)
        .filter((k) => k.startsWith('sidestreet_pdf_export_'))
        .forEach((k) => sessionStorage.removeItem(k))
    } catch (_) {}
  }, [])

  const [jobs, setJobs] = useState([])
  // Map of jobId -> intervalId for independent per-job polling
  const pollRefs = useRef(new Map())

  const stopPolling = useCallback((jobId) => {
    if (pollRefs.current.has(jobId)) {
      clearInterval(pollRefs.current.get(jobId))
      pollRefs.current.delete(jobId)
    }
  }, [])

  const triggerDownload = useCallback(async (jobId) => {
    if (_downloadTriggered.has(jobId)) return
    _downloadTriggered.add(jobId)
    const dl = await downloadPdf(jobId)
    if (!dl.success) {
      _downloadTriggered.delete(jobId)
      setJobs((prev) =>
        prev.map((j) =>
          j.jobId === jobId ? { ...j, status: 'failed', error: dl.error } : j
        )
      )
    } else {
      setTimeout(() => {
        setJobs((prev) => prev.filter((j) => j.jobId !== jobId))
      }, 4000)
    }
  }, [])

  const startPolling = useCallback(
    (jobId) => {
      if (pollRefs.current.has(jobId)) return

      const intervalId = setInterval(async () => {
        if (_downloadTriggered.has(jobId)) { stopPolling(jobId); return }
        const result = await pollPdfStatus(jobId)
        setJobs((prev) => prev.map((j) => (j.jobId === jobId ? { ...j, ...result } : j)))

        if (result.status === 'completed') {
          stopPolling(jobId)
          triggerDownload(jobId)
        } else if (result.status === 'failed') {
          stopPolling(jobId)
        }
      }, 2000)

      pollRefs.current.set(jobId, intervalId)
    },
    [stopPolling, triggerDownload]
  )

  const submit = useCallback(
    async (payload, options = {}) => {
      const idem = options.idempotencyKey
      if (idem) {
        if (Date.now() < (_pdfIdemCooldownUntil.get(idem) || 0)) {
          return { success: false, skipped: true }
        }
        if (_pdfIdemInFlight.has(idem)) {
          return { success: false, skipped: true }
        }
        _pdfIdemInFlight.add(idem)
      }

      // Add a temporary placeholder so the toast appears immediately
      const tempId = `submitting-${Date.now()}`
      setJobs((prev) => [
        ...prev,
        { jobId: tempId, status: 'submitting', position: null, error: null },
      ])

      let result
      try {
        result = await submitPdfJob(payload)
      } finally {
        if (idem) _pdfIdemInFlight.delete(idem)
      }

      if (!result.success) {
        setJobs((prev) =>
          prev.map((j) =>
            j.jobId === tempId
              ? { ...j, status: 'failed', error: result.error, noCredits: result.noCredits }
              : j
          )
        )
        return result
      }

      if (idem) {
        _pdfIdemCooldownUntil.set(idem, Date.now() + PDF_IDEM_COOLDOWN_MS)
      }

      // Replace temp placeholder with real job
      setJobs((prev) =>
        prev.map((j) =>
          j.jobId === tempId
            ? {
                jobId: result.jobId,
                status: 'queued',
                position: result.position,
                creditsRemaining: result.creditsRemaining,
                error: null,
              }
            : j
        )
      )
      startPolling(result.jobId)
      return result
    },
    [startPolling]
  )

  const redownload = useCallback(
    async (jobId) => {
      if (_downloadTriggered.has(jobId)) return
      setJobs((prev) => [
        ...prev.filter((j) => j.jobId !== jobId),
        { jobId, status: 'completed', position: null, error: null },
      ])
      await triggerDownload(jobId)
    },
    [triggerDownload]
  )

  const dismiss = useCallback(
    (jobId) => {
      stopPolling(jobId)
      setJobs((prev) => prev.filter((j) => j.jobId !== jobId))
    },
    [stopPolling]
  )

  return (
    <PdfExportContext.Provider value={{ jobs, submit, dismiss, redownload }}>
      {children}
    </PdfExportContext.Provider>
  )
}

export function usePdfExport() {
  const ctx = useContext(PdfExportContext)
  if (!ctx) throw new Error('usePdfExport must be used within PdfExportProvider')
  return ctx
}
